import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { ImQuotesLeft } from "react-icons/im";
import Footer from "./Footer";

export default function Testimonials({ isHomepagePreview }) {
  const appContext = useContext(AppContext);
  const isTestimonialsPage = appContext?.navItem === "testimonials";

  const [ready, setReady] = useState(false);
  const [current, setCurrent] = useState(0);

  async function init() {
    setReady(false);
    if (appContext?.getAllTestimonials) {
      await appContext.getAllTestimonials();
    }
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  // Safely extract list from direct array or API wrapper `{ status: 1, data: [...] }`
  const testimonialsList = useMemo(() => {
    const raw = appContext?.allTestimonials || appContext?.testimonials || [];
    return Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
  }, [appContext?.allTestimonials, appContext?.testimonials]);

  const handleNext = () => {
    if (testimonialsList.length === 0) return;
    setCurrent((prev) => (prev + 1) % testimonialsList.length);
  };

  const handlePrev = () => {
    if (testimonialsList.length === 0) return;
    setCurrent((prev) => (prev - 1 + testimonialsList.length) % testimonialsList.length);
  };

  if (!ready) {
    return (
      <div className="container py-5 text-center">
        {getInlineLoader ? getInlineLoader() : <div className="text-muted">Loading...</div>}
      </div>
    );
  }

  const activeItem = testimonialsList[current];

  // --- VERSION 1: HOMEPAGE PREVIEW CARD ---
  if (isHomepagePreview) {
    return (
      <div
        className="card border-0 shadow-sm overflow-hidden mt-5"
        style={{
          borderRadius: "20px",
          minHeight: "450px",
          background: "#fff",
          paddingTop: "var(--topBarHeight, 0px)",
        }}
      >
        <div className="card-header bg-white border-0 p-4 mt-2">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="fw-bold mb-0" style={{ color: "var(--primaryColor)", letterSpacing: "1px" }}>
              TESTIMONIALS
            </h6>
            {testimonialsList.length > 1 && (
              <div className="d-flex gap-2">
                <button onClick={handlePrev} className="btn-nav-circle" aria-label="Previous">
                  <FaChevronLeft size={12} />
                </button>
                <button onClick={handleNext} className="btn-nav-circle" aria-label="Next">
                  <FaChevronRight size={12} />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="card-body p-4 d-flex flex-column justify-content-center">
          {testimonialsList.length > 0 ? (
            <div className="text-center transition-fade" key={current}>
              <ImQuotesLeft size={30} className="mb-3" style={{ color: "var(--primaryColor)", opacity: 0.8 }} />
              <div
                className="mb-3 text-secondary italic"
                style={{ fontSize: "1rem", lineHeight: "1.5" }}
                dangerouslySetInnerHTML={{ __html: activeItem?.testimony || activeItem?.quote || "" }}
              />
              <h6 className="fw-bold mb-0" style={{ color: "#333" }}>
                {activeItem?.clientName || activeItem?.name || "Anonymous"}
              </h6>
              {activeItem?.clientOrganization && (
                <small className="text-muted text-uppercase" style={{ fontSize: "0.75rem", letterSpacing: "1px" }}>
                  {activeItem.clientOrganization}
                </small>
              )}
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              <p className="mb-0">No testimonials found.</p>
            </div>
          )}
        </div>

        <style>{`
          .btn-nav-circle {
            width: 32px; height: 32px; border-radius: 50%; border: 1px solid #eee;
            background: #fff; color: #555; display: flex; align-items: center; justify-content: center;
            transition: all 0.2s; cursor: pointer;
          }
          .btn-nav-circle:hover { background: var(--primaryColor); color: #fff; border-color: var(--primaryColor); }
          .transition-fade { animation: fadeIn 0.4s ease-in-out; }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // --- VERSION 2: FULL TESTIMONIALS PAGE ---
  return (
    <div className="MainBodySection" style={{ paddingBottom: "75px" }}>

      <div className="container py-4">
        {testimonialsList.length > 0 ? (
          <div className="card border-0 shadow-sm p-4 p-md-5 mx-auto" style={{ maxWidth: "800px", borderRadius: "20px" }}>
            <div className="text-center">
              <ImQuotesLeft size={36} className="mb-4" style={{ color: "var(--primaryColor)" }} />
              <div
                className="fs-4 italic mb-4"
                style={{ color: "#334155" }}
                dangerouslySetInnerHTML={{ __html: activeItem.testimony }}
              />
              <h5 className="fw-bold mb-1">{activeItem?.clientName || activeItem?.name}</h5>
              <p className="text-muted text-uppercase small mb-4">{activeItem?.clientOrganization}</p>

            </div>
            <div className="d-flex justify-content-center gap-3 mt-3">
              <button onClick={handlePrev} className="btn border px-2">
                <FaChevronLeft size={16} />
              </button>
              <button onClick={handleNext} className="btn border px-2">
                <FaChevronRight size={16} />
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-5">
            <h6>No data was found</h6>
          </div>
        )}
      </div>

      {isTestimonialsPage && <Footer />}
    </div>
  );
}